import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  EVENT_BUILDER_PREVIEW_MESSAGE,
  EVENT_BUILDER_PREVIEW_READY_MESSAGE,
  type EventBuilderPreviewPayload,
} from '@conference/shared';
import type { EventBuilderDraft } from '../services/eventBuilderService';

function previewTarget(attendeeUrl?: string) {
  if (!attendeeUrl) return null;
  try {
    const base = new URL(attendeeUrl);
    return {
      origin: base.origin,
      src: new URL('/builder-preview.html', base).toString(),
    };
  } catch {
    return null;
  }
}

export default function EventBuilderAttendeePreview({
  draft,
  attendeeUrl = import.meta.env.VITE_ATTENDEE_URL || (import.meta.env.DEV ? 'http://localhost:5173' : undefined),
}: {
  draft: EventBuilderDraft | null;
  attendeeUrl?: string;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const target = useMemo(() => previewTarget(attendeeUrl), [attendeeUrl]);
  const designReady = Boolean(draft && (
    draft.template_state.mode === 'custom'
    || draft.template_state.selected_template !== null
  ));
  const payload = useMemo<EventBuilderPreviewPayload | null>(() => draft && designReady ? ({
    version: 1,
    revision: draft.revision,
    requirements: draft.requirements,
    blueprint: draft.blueprint,
  }) : null, [designReady, draft]);
  const post = useCallback(() => {
    if (!target || !payload) return;
    frame.current?.contentWindow?.postMessage({
      type: EVENT_BUILDER_PREVIEW_MESSAGE,
      payload,
    }, target.origin);
  }, [payload, target]);

  useEffect(post, [post]);
  useEffect(() => {
    if (!target) return;
    const onMessage = (event: MessageEvent) => {
      if (
        event.origin === target.origin
        && event.source === frame.current?.contentWindow
        && event.data?.type === EVENT_BUILDER_PREVIEW_READY_MESSAGE
      ) post();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [post, target]);

  if (!draft) return <div className="flex min-h-[560px] items-center justify-center rounded-xl bg-gray-950 p-6 text-center text-sm text-gray-500">Describe your event to generate the attendee preview.</div>;
  if (!payload) return <div className="flex min-h-[560px] items-center justify-center rounded-xl bg-gray-950 p-6 text-center text-sm text-gray-500">Preview a template or customize with AI to see the attendee app.</div>;
  if (!target) return <div className="flex min-h-[560px] items-center justify-center rounded-xl bg-gray-950 p-6 text-center text-sm text-gray-500">Attendee preview is unavailable. Configure VITE_ATTENDEE_URL to enable it.</div>;

  return <div className="mx-auto w-full max-w-[398px] overflow-hidden rounded-[2rem] border-4 border-gray-800 bg-gray-950 shadow-xl">
    <iframe
      ref={frame}
      title="Attendee app preview"
      src={target.src}
      sandbox="allow-scripts allow-same-origin"
      onLoad={post}
      className="block h-[680px] w-[390px] max-w-full border-0"
    />
  </div>;
}
